/**
 * Navigation utilities to handle c_selections parameter preservation
 *
 * When app is embedded in Checkmate platform, the c_selections parameter
 * must be preserved across all navigation to maintain context
 *
 * Note: Only active in production/Checkmate environments, not in local development
 */

import { isCheckmated } from '../services/checkmate'

const C_SELECTIONS_KEY = 'c_selections'

/**
 * Check if we're in local development
 */
function isDevelopment(): boolean {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
}

/**
 * Get current c_selections from URL or localStorage
 * Returns null in development environment
 */
export function getCSelections(): string | null {
  // Skip in development
  if (isDevelopment()) {
    return null
  }

  // First try URL params
  const urlParams = new URLSearchParams(window.location.search)
  const fromUrl = urlParams.get(C_SELECTIONS_KEY)

  if (fromUrl) {
    // Store in localStorage for future use
    localStorage.setItem(C_SELECTIONS_KEY, fromUrl)
    return fromUrl
  }

  // Fallback to localStorage
  return localStorage.getItem(C_SELECTIONS_KEY)
}

/**
 * Build a full URL for use in href attributes (supports right-click → Open in New Tab).
 *
 * In Checkmate mode (hash routing), builds:
 *   /apps/edge-rcm/app?c_selections=...#/!/app/show-file/123
 *
 * In standalone/DSO mode (browser routing), builds:
 *   /show-file/123?c_selections=...
 *
 * NOTE: Do NOT pass this to React Router's navigate(). Use buildRouterPath() instead.
 */
export function buildUrlWithCSelections(path: string, additionalParams?: Record<string, string>): string {
  const cSelections = getCSelections()

  // Parse existing query params from path
  const [basePath, existingQuery] = path.split('?')
  const params = new URLSearchParams(existingQuery || '')

  // Add any additional params first (these should be encoded)
  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      params.set(key, value)
    })
  }

  // In Checkmate mode, build hash-routed URL:
  // {appBasePath}?{c_selections}&{other_params}#/!/app{routePath}
  if (isCheckmated()) {
    // Only c_selections goes in the outer query (before #) — React Router hash routing
    // does not parse params before the #, so route-level params must stay inside the hash.
    const outerQuery = cSelections ? `${C_SELECTIONS_KEY}=${cSelections}` : ''

    // Route-level params stay inside the hash so React Router (useSearchParams) can see them
    const routeParams = params.toString()
    const hashRoute = `#/!/app${basePath}${routeParams ? '?' + routeParams : ''}`

    // Get the app base path from current location (e.g. /apps/edge-rcm/app)
    const appBasePath = window.location.pathname

    return `${appBasePath}${outerQuery ? '?' + outerQuery : ''}${hashRoute}`
  }

  // Standalone/DSO mode — plain path with optional query params
  let queryString = params.toString()

  // Add c_selections manually without encoding to match production format
  // c_selections uses colons and commas which don't need encoding in URLs
  if (cSelections) {
    const separator = queryString ? '&' : ''
    queryString = queryString
      ? `${queryString}${separator}${C_SELECTIONS_KEY}=${cSelections}`
      : `${C_SELECTIONS_KEY}=${cSelections}`
  }

  return queryString ? `${basePath}?${queryString}` : basePath
}

/**
 * Build a route path for use with React Router's navigate().
 * Returns an internal path with c_selections as a query param — NOT a full MFE URL.
 *
 * Use buildUrlWithCSelections() for href attributes instead.
 */
export function buildRouterPath(path: string, additionalParams?: Record<string, string>): string {
  const cSelections = getCSelections()

  // Parse existing query params from path
  const [basePath, existingQuery] = path.split('?')
  const params = new URLSearchParams(existingQuery || '')

  // Add any additional params
  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      params.set(key, value)
    })
  }

  // Remove c_selections from params before serializing — it may have been added by the
  // caller (e.g. useNavigateWithParams) AND will be re-appended below without encoding.
  // Removing it here prevents duplicates and ensures a single consistent unencoded value.
  params.delete(C_SELECTIONS_KEY)

  let queryString = params.toString()

  // Add c_selections manually without encoding to preserve colons/commas as-is
  if (cSelections) {
    const separator = queryString ? '&' : ''
    queryString = queryString
      ? `${queryString}${separator}${C_SELECTIONS_KEY}=${cSelections}`
      : `${C_SELECTIONS_KEY}=${cSelections}`
  }

  return queryString ? `${basePath}?${queryString}` : basePath
}

/**
 * Navigate to a path while preserving c_selections
 * Use this wrapper instead of direct navigate()
 */
export function navigateWithCSelections(
  navigate: (to: string) => void,
  path: string,
  additionalParams?: Record<string, string>
): void {
  const fullPath = buildUrlWithCSelections(path, additionalParams)
  navigate(fullPath)
}

/**
 * Check if we're running in Checkmate environment
 * Always false in local development
 */
export function isCheckmateEnvironment(): boolean {
  if (isDevelopment()) {
    return false
  }
  return !!getCSelections()
}

/**
 * Get all preserved query params for navigation
 * Returns an object with c_selections and any other params to preserve
 */
export function getPreservedParams(): Record<string, string> {
  const params: Record<string, string> = {}
  const cSelections = getCSelections()

  if (cSelections) {
    params[C_SELECTIONS_KEY] = cSelections
  }

  return params
}
